import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ReplayIcon from '@mui/icons-material/Replay';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { HousekeepingList, OnlyFansSubscriber } from '../../../hooks/useSubscriber';
import useSubscriberLists from '../../../hooks/useSubscriberLists';
import { UserContext } from '../../../store/UserContext';
import { OnlyFansLists } from '../../../types/onlyFansTypes';
import CopyToClipboardButton from '../../common/CopyToClipboardButton';
import OnlyFansListsMultiSelector from '../../common/OnlyFansListsMultiSelector';
import StyledCard from '../../common/StyledCard';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';
import TextInputDialog from '../../common/TextInputDialog';
import SextforceLiveStreamAddTippersToListCreateListDialog from '../hostess/liveStreams/addTippersToList/SextforceLiveStreamAddTippersToListCreateListDialog';

const Row = ({
    list,
    onlyFansLists,
    onlyFansListsLoading,
    renameList,
    isRenamingList,
    createHousekeepingList,
    isCreatingList,
    refetchLists,
    handleSubscriberRefetch,
}: {
    list: HousekeepingList;
    onlyFansLists: OnlyFansLists.List[];
    onlyFansListsLoading: boolean;
    renameList: (listId: string | number, name: string) => Promise<OnlyFansLists.List | null>;
    isRenamingList: boolean;
    createHousekeepingList: (listName: string) => Promise<OnlyFansLists.List | null>;
    isCreatingList: boolean;
    refetchLists: () => void;
    handleSubscriberRefetch: () => void;
}) => {
    const userContext = useContext(UserContext);
    const { enqueueSnackbar } = useSnackbar();

    const [renameListDialogOpen, setRenameListDialogOpen] = useState(false);

    const handleRenameList = (value: string) => {
        console.log('Renaming list', list.listId, 'to', value);
        if (!list.listId || !value.trim()) {
            return;
        }

        renameList(list.listId, value)
            .then(() => {
                enqueueSnackbar(`List renamed to "${value}"`, {
                    variant: 'success',
                    autoHideDuration: 2000,
                });

                handleSubscriberRefetch();
                setRenameListDialogOpen(false);
            })
            .catch(error => {
                enqueueSnackbar(`Error renaming list: ${error.message}`, {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            });
    };

    const foundList = onlyFansLists?.find(
        l => l.type === 'custom' && (typeof l.id === 'string' ? Number(l.id) : l.id) === Number(list.listId),
    );

    const handleCreateMissingList = () => {
        createHousekeepingList(list.listName).then(newList => {
            if (!newList) {
                enqueueSnackbar(`Failed to create list "${list.listName}"`, { variant: 'error' });

                return;
            }

            enqueueSnackbar(`List "${list.listName}" created and assigned`, {
                variant: 'success',
                autoHideDuration: 2000,
            });

            refetchLists();
            handleSubscriberRefetch();
        });
    };

    return (
        <>
            <TableRow>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CopyToClipboardButton text={foundList?.id.toString() || ''} disabled={!foundList} />
                        <Typography variant="body1" noWrap>
                            {list.listName}
                        </Typography>
                    </Stack>
                </TableCell>

                <TableCell align="center">
                    {list.addPrefixToUserDisplayName && list.userDisplayNamePrefix ? list.userDisplayNamePrefix.trim() : ''}
                </TableCell>
                <TableCell align="center">{foundList?.usersCount.toLocaleString() || <CircularProgress size={14} />}</TableCell>
                <TableCell align="center">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        {userContext.isAdmin && !foundList && (
                            <Tooltip title="Create this missing list on OnlyFans and assign it (admin only)" arrow>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="secondary"
                                        disabled={onlyFansListsLoading || isCreatingList}
                                        onClick={handleCreateMissingList}
                                    >
                                        {isCreatingList ? <CircularProgress size={14} /> : <PlaylistAddIcon fontSize={'small'} />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Rename list" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    disabled={
                                        !list.listId ||
                                        !onlyFansLists?.find(
                                            l =>
                                                l.type === 'custom' &&
                                                (typeof l.id === 'string' ? Number(l.id) : l.id) === Number(list.listId),
                                        )
                                    }
                                    onClick={() => setRenameListDialogOpen(true)}
                                >
                                    <DriveFileRenameOutlineIcon fontSize={'small'} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Open list in new window" arrow>
                            <span>
                                <IconButton
                                    component={Link}
                                    to={`https://onlyfans.com/my/collections/${list.listId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    disabled={
                                        !list.listId ||
                                        onlyFansListsLoading ||
                                        !onlyFansLists?.find(
                                            l =>
                                                l.type === 'custom' &&
                                                (typeof l.id === 'string' ? Number(l.id) : l.id) === Number(list.listId),
                                        )
                                    }
                                >
                                    <OpenInNewIcon fontSize={'small'} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </TableCell>
            </TableRow>
            {renameListDialogOpen && !onlyFansListsLoading && (
                <TextInputDialog
                    open={renameListDialogOpen}
                    onClose={() => setRenameListDialogOpen(false)}
                    initialValue={list.listName}
                    cancelButtonText="Cancel"
                    submitButtonText="Rename"
                    title="Rename List"
                    contentText="Enter a new name for the list."
                    onSubmit={(value: string) => {
                        handleRenameList(value);
                    }}
                    isLoading={isRenamingList}
                />
            )}
        </>
    );
};

const HousekeepingCurrentLists = ({
    subscriber,
    handleSubscriberRefetch,
}: {
    subscriber: OnlyFansSubscriber;
    handleSubscriberRefetch: () => void;
}) => {
    const userContext = useContext(UserContext);
    const theme: Theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [isCreateListDialogOpen, setIsCreateListDialogOpen] = useState(false);

    const {
        lists,
        refetch: refetchLists,
        isLoading: listsLoading,
        isFetching: listsFetching,
        loadedListsCount,
        hasMore,
        renameList,
        isRenamingList,
        createList,
        createHousekeepingList,
        isCreatingList,
    } = useSubscriberLists(true);

    const isLoading = listsLoading || listsFetching || hasMore;

    const handleCreateList = (listName: string) => {
        createList(listName)
            .then(newList => {
                if (!newList) {
                    enqueueSnackbar('Failed to create list!', { variant: 'error' });
                    return;
                }

                enqueueSnackbar('List created!', { variant: 'success' });

                setIsCreateListDialogOpen(false);
            })
            .then(() => {
                refetchLists();
            });
    };

    return (
        <Box>
            <StyledCard noCard noBottomMargin>
                <StyledCardTitleBar
                    title="Housekeeping Lists"
                    isLoading={isLoading || isRenamingList}
                    badge={
                        <Tooltip title="Refresh lists" arrow>
                            <span>
                                <IconButton color="secondary" onClick={refetchLists}>
                                    <ReplayIcon color="secondary" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    }
                />
            </StyledCard>

            <StyledCard>
                <Stack direction="column" spacing={2} alignItems="flex-start">
                    <Typography variant="body1">
                        These lists are created and updated by the Housekeeping service. Please don't modify or delete them yourself. Please
                        only rename them using the rename button below. For any other changes, please contact support via Telegram.
                    </Typography>

                    {subscriber?.housekeeping?.lists && Array.isArray(subscriber.housekeeping.lists) && (
                        <Box
                            sx={{
                                overflow: 'auto',
                                width: '100%',
                            }}
                        >
                            <Table
                                sx={{
                                    // Remove left padding from the first cell
                                    '& td:first-of-type': {
                                        paddingLeft: 0,
                                    },
                                    // Remove right padding from the last cell
                                    '& td:last-of-type': {
                                        paddingRight: 0,
                                    },
                                    // Make last column sticky
                                    '& th:last-of-type, & td:last-of-type': {
                                        position: 'sticky',
                                        right: 0,
                                        backgroundColor: 'background.paper',
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        // Remove top padding from the first cell header
                                        '& th:first-of-type': {
                                            paddingLeft: 0,
                                        },
                                        // Remove bottom padding from the last cell header
                                        '& th:last-of-type': {
                                            paddingRight: 0,
                                        },
                                        // bold headers
                                        '& th': {
                                            fontWeight: 'bold',
                                        },
                                    }}
                                >
                                    <TableRow>
                                        <TableCell>List Name</TableCell>
                                        <TableCell width={90} align="center">
                                            Prefix
                                        </TableCell>
                                        <TableCell width={90} align="center">
                                            Users
                                        </TableCell>
                                        <TableCell width={90} align="center">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subscriber.housekeeping.lists.map((list, index) => (
                                        <Row
                                            key={index}
                                            list={list}
                                            onlyFansLists={lists}
                                            onlyFansListsLoading={isLoading}
                                            renameList={renameList}
                                            isRenamingList={isRenamingList}
                                            createHousekeepingList={createHousekeepingList}
                                            isCreatingList={isCreatingList}
                                            refetchLists={refetchLists}
                                            handleSubscriberRefetch={handleSubscriberRefetch}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {(!subscriber?.housekeeping?.lists ||
                        !Array.isArray(subscriber.housekeeping.lists) ||
                        subscriber.housekeeping.lists.length === 0) && (
                        <Typography variant="body2" color="text.secondary">
                            No Housekeeping lists setup
                        </Typography>
                    )}
                </Stack>
            </StyledCard>

            <StyledCard noCard noBottomMargin>
                <StyledCardTitleBar
                    title={`OnlyFans Lists (${loadedListsCount.toLocaleString()})`}
                    badge={
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => {
                                setIsCreateListDialogOpen(true);
                            }}
                        >
                            Create
                        </Button>
                    }
                />
            </StyledCard>

            <StyledCard>
                <Stack direction="column" spacing={2} alignItems="flex-start">
                    <Typography variant="body1">
                        These are all your lists on OnlyFans, including those not used by the Housekeeping service.
                    </Typography>
                    <OnlyFansListsMultiSelector
                        height={400}
                        showListIdCopyButton={userContext.isAdmin || false}
                        showDelete={userContext.isAdmin || false}
                    />
                    {isCreateListDialogOpen && (
                        <SextforceLiveStreamAddTippersToListCreateListDialog
                            open={isCreateListDialogOpen}
                            onClose={() => setIsCreateListDialogOpen(false)}
                            handleSubmit={handleCreateList}
                            theme={theme}
                        />
                    )}
                </Stack>
            </StyledCard>
        </Box>
    );
};

export default HousekeepingCurrentLists;
